import Vue from 'vue'
import { normalizeAndroidScanOptions } from '@/untils/device'

function setupWebViewJavascriptBridge (callback) {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1 // android终端
  if (isAndroid) {
    if (window.WebViewJavascriptBridge) {
      callback(window.WebViewJavascriptBridge)
    } else {
      document.addEventListener('WebViewJavascriptBridgeReady', () => {
        callback(window.WebViewJavascriptBridge)
      }, false)
    }
  } else {
    if (window.WebViewJavascriptBridge) {
      return callback(window.WebViewJavascriptBridge)
    }
    if (window.WVJBCallbacks) {
      return window.WVJBCallbacks.push(callback)
    }
    window.WVJBCallbacks = [callback]
    const WVJBIframe = document.createElement('iframe')
    WVJBIframe.style.display = 'none'
    WVJBIframe.src = 'https://__bridge_loaded__'
    document.documentElement.appendChild(WVJBIframe)
    setTimeout(() => {
      document.documentElement.removeChild(WVJBIframe)
    }, 0)
  }
}

/**
 * @function getAvailableNativeBridge
 * @description 会员专用：取当前可用原生桥（WVJB / BridgeYes）
 * @returns {Object|null}
 */
function getAvailableNativeBridge () {
  if (window.WebViewJavascriptBridge && typeof window.WebViewJavascriptBridge.callHandler === 'function') {
    return window.WebViewJavascriptBridge
  }
  if (window.bridgeYesApi && typeof window.bridgeYesApi.callHandler === 'function') {
    return window.bridgeYesApi
  }
  return null
}

/**
 * @function describeNativeBridge
 * @param {Object|null} bridge
 * @returns {string}
 */
function describeNativeBridge (bridge) {
  if (!bridge) return 'null'
  if (bridge === window.WebViewJavascriptBridge) return 'WVJB'
  if (bridge === window.bridgeYesApi) return 'bridgeYes'
  return 'unknown'
}

/**
 * @function setupMemberWebViewBridge
 * @description 会员专用桥就绪：兼容 BridgeYes / 双端轮询兜底，不影响全局 setupWebViewJavascriptBridge
 * @param {Function} callback
 */
function setupMemberWebViewBridge (callback) {
  const ready = getAvailableNativeBridge()
  if (ready) {
    callback(ready)
    return
  }

  let done = false
  const fire = (bridge) => {
    if (done) return
    const target = bridge && typeof bridge.callHandler === 'function'
      ? bridge
      : getAvailableNativeBridge()
    if (!target) return
    done = true
    callback(target)
  }

  document.addEventListener('BridgeYesApiReady', () => {
    fire(window.bridgeYesApi)
  }, false)

  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Adr') > -1
  if (isAndroid) {
    document.addEventListener('WebViewJavascriptBridgeReady', () => {
      fire(window.WebViewJavascriptBridge)
    }, false)
  } else {
    // iOS：走 WVJB 标准注入；同时不 return，下面继续轮询 BridgeYes
    if (window.WVJBCallbacks) {
      window.WVJBCallbacks.push(fire)
    } else {
      window.WVJBCallbacks = [fire]
      const WVJBIframe = document.createElement('iframe')
      WVJBIframe.style.display = 'none'
      WVJBIframe.src = 'https://__bridge_loaded__'
      document.documentElement.appendChild(WVJBIframe)
      setTimeout(() => {
        document.documentElement.removeChild(WVJBIframe)
      }, 0)
    }
  }

  // 双端轮询：BridgeYes 晚注入或未派发 Ready 时，iOS 原先会永久挂起
  let tries = 0
  const timer = setInterval(() => {
    if (done) {
      clearInterval(timer)
      return
    }
    const bridge = getAvailableNativeBridge()
    if (bridge) {
      clearInterval(timer)
      fire(bridge)
    } else if (++tries >= 40) {
      clearInterval(timer)
    }
  }, 50)
}

/**
 * @function pickAuthNativeBridge
 * @description 授权桥：iOS 优先 BridgeYes（部分壳有 WVJB stub 但无 getAuthFromThirdPartApp）
 * @param {Object|null} fallback
 * @returns {Object|null}
 */
function pickAuthNativeBridge (fallback) {
  const yes = window.bridgeYesApi && typeof window.bridgeYesApi.callHandler === 'function'
    ? window.bridgeYesApi
    : null
  const u = navigator.userAgent || ''
  const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  if (isIOS && yes) return yes
  if (fallback && typeof fallback.callHandler === 'function') return fallback
  return getAvailableNativeBridge()
}

/**
 * @function isIOSDevice
 * @returns {boolean}
 */
function isIOSDevice () {
  return !!((navigator.userAgent || '').match(/\(i[^;]+;( U;)? CPU.+Mac OS X/))
}

/**
 * @function openAlipayAuthViaNative
 * @description iOS 勿用 location / a 标签打开 alipays://（WebView 打开会触发「回跳地址不一致」）。
 * 须走原生 openURL（jumpToBrowser），与 Android getAuthFromThirdPartApp 同类。
 * @param {Object} bridge
 * @param {string} authUrl
 */
function openAlipayAuthViaNative (bridge, authUrl) {
  if (!authUrl) return
  const target = (bridge && typeof bridge.callHandler === 'function')
    ? bridge
    : getAvailableNativeBridge()
  if (!target || typeof target.callHandler !== 'function') {
    console.error('[bridge] openAlipayAuthViaNative.unavailable')
    return
  }
  console.log('[bridge] openAlipayAuthViaNative jumpToBrowser')
  target.callHandler('jumpToBrowser', { url: authUrl }, function (res) {
    console.log('[bridge] jumpToBrowser.cb', res)
  })
}

/**
 * @function scheduleIOSAuthNativeFallback
 * @description getAuthFromThirdPartApp 空跑时，用原生 jumpToBrowser 打开 authUrl；页面已 hidden 则跳过
 * @param {Object} bridge
 * @param {string} authUrl
 */
function scheduleIOSAuthNativeFallback (bridge, authUrl) {
  if (!isIOSDevice() || !authUrl) return
  setTimeout(() => {
    if (typeof document !== 'undefined' && document.hidden) {
      console.log('[bridge] getAuthFromThirdPartApp.skipNativeFallback (page hidden)')
      return
    }
    console.log('[bridge] getAuthFromThirdPartApp.nativeFallback')
    openAlipayAuthViaNative(bridge, authUrl)
  }, 450)
}

/**
 * @function parseBridgeScanResult
 * @param {any} res
 * @returns {any}
 */
function parseBridgeScanResult (res) {
  if (typeof res === 'string' && /^\{.+\}$/.test(res)) {
    try {
      return JSON.parse(res)
    } catch (e) { /* keep string */ }
  }
  return res
}

/**
 * @func 调用原生的方法
 * @param {String} fun 桥接名
 * @param {Object} params 参数
 * @param {Bollean} hasCallback 原生是否执行我们的回调函数
 * @author yyc
 */
function invoke(fun, params = '', hasCallback = false) {
  console.log('invoke', fun, params, hasCallback)
  return new Promise((resolve, reject) => {
    try {
      setupWebViewJavascriptBridge(function (bridge) {
        bridge.callHandler(fun, params, function (res) {
          // 如果有回调就从回调中resolve
          console.log('*******', res)
          if (typeof res === 'string' && /^\{.+\}$/.test(res)) {
            res = JSON.parse(res)
          }
          hasCallback && resolve(res)
        })
      })
      // 如果没有回调就直接resolve
      !hasCallback && resolve()
    } catch (err) {
      reject(err)
    }
  })
}

/**
 * @func 原生调用我们的方法
 * @param {String} name 桥接名
 * @author yyc
 */
function register(name) {
  return new Promise((resolve, reject) => {
    try {
      setupWebViewJavascriptBridge((bridge) => {
        bridge.registerHandler(name, (res) => {
          // resolve(typeof res === 'string' ? JSON.parse(res) : res)
          if (typeof res === 'string' && /^\{.+\}$/.test(res)) {
            res = JSON.parse(res)
          }
          resolve(res)
        })
      })
    } catch (err) {
      reject(err)
    }
  })
}

const ua = navigator.userAgent.toLowerCase()
const isAndroid = ua.indexOf('android') >= 0
const isIOS = ua.indexOf('iphone') >= 0 || ua.indexOf('ipad') >= 0

const apiList = {
  isAndroid,
  isIOS,
  /**
  * @func 调用原生相机
  * @param  {Object} type 0-相机拍摄 1-从相册上传 2-OCR相机
  * @param  {OBject} identityType 0-身份证正面 1-身份证反面
  * @param  {Object} width 默认0
  * @param  {Object} height 默认0
  * @param  {Object} cutting  cutting代表是否裁剪
  */
  async imageSelectFunc (params) {
    await invoke('imageSelectFunc', params)
    return register('returnImagesForJs')
  },

  /**
   * @func 图片预览
   */
  async jumpToPreviewImages (params) {
    await invoke('jumpToPreviewImages', params)
    return register('returnImagesForJs')
  },

  /**
   * @func Bi统计数据埋点上报
   */
  reportBiStatisticsFunc: (params) => invoke('reportBiStatisticsFunc', params),

  /**
   * @func 获取app通用信息版本号
   */
  getAppInfoFunc: (params) => invoke('getAppInfoFunc', params, true),

  /**
   * @func 跳转外链给到原生桥接
   */
  jumpOutUrl: (params) => invoke('jumpUrl', params),

  /**
   * @func 跳转到原生地图导航
   */
  jumpMapNavigation: (params) => invoke('jumpMapNavigation', params),

  /**
   * @func 跳转到七鱼客服
   */
  getQYServer: () => invoke('getQYServer'),
  /**
   * @func 跳转到七鱼客服（ios 4.0以上版本支持 新桥接）
   */
  pushQYServer: () => invoke('pushQYServer'),

  /**
   * @func 修改电池栏颜色
   */
  modifyStatusBarFunc: (params) => invoke('modifyStatusBarFunc', params),

  /**
   * @func 返回到原生页面
   */
  backNativeApp: (params) => invoke('toReturnApp', params),

  /**
   * @func 获取门店信息
   */
  getCurrentStoreInfo: () => invoke('getCurrentStoreInfo', '', true),

  /**
   * @func 分享微信/支付宝
   */
  onAppShare: (params) => invoke('shareFunc', params),

  /**
   * @func 保存图片
   */
  downloadImage: (params) => invoke('downloadImagesFunc', params),

  /**
   * @func 跳转到具体的账单详情
   */
  jumpToBillDetail: (params) => invoke('jumpBillDetail', params),

  /**
   * @func 导出url, 参数copyUrl：'' 具体的url
   */
  copyExportUrl: (params) => invoke('copyExportUrl', params),
  /**
   * @func 跳转到绑定银行卡
   */
  jumpToBindCard: () => invoke('MyCardPage'),

  /**
   * @func 跳转到扫码点单
   */
  jumpToScanOrder: (params) => invoke('jumpToScanOrder', params),

  /**
   * @func 升级app
   */
  appUpdate: () => invoke('exeUpgrade'),

  /**
   * @func 获取用户信息
   */
  getUserInfo: (params) => invoke('getInfo', params, true),

  /**
   * @func 跳转到angular页面
   */
  jumpToAngularPage: (params) => invoke('jumpToAngularPage', params),

  /**
   * @func 跳转到新版angular页面
   */
  jumpToNewAngularPage: (params) => invoke('jumpToNewAngularPage', params),

  /**
   * @func 选择时间
   */
  async openTimePicker(params) {
    await invoke('openTimePicker', params)
    return register('pushCheckedTime')
  },

  /**
   * @func 选择日期
   */
  async openDatePicker(params) {
    await invoke('openDatePicker', params)
    return register('pushCheckedDate')
  },

  /**
   * @func 跳转到第三方浏览器
   */
  jumpToBrowser: (params) => invoke('jumpToBrowser', params),

  /**
   * @func 跳转到360贷款
   */
  jumpTo360Loan: () => invoke('jumpTo360Loan'),

  /**
   * @func 设置导航栏右边文字
   */
  setNavigationBarRightDo: (params) => invoke('setNavigationBarRightDo', params),

  /**
   * @func 设置导航栏title
   */
  setNavigationBarTitle: (params) => invoke('setNavigationBarTitle', params),

  /**
   * @func 友盟埋点
   */
  reportUmengEvent: (params) => invoke('reportUmengEvent', params),

  /**
   * @func 选择日期桥接
   */
  async selectCalendar (params) {
    await invoke('jumpToCalendar', params)
    return register('putCalendarRange')
  },

  /**
   * @func 选择的门店列表
   */
  selectStoreInfo: (params) => invoke('selectStoreInfo', params),

  /**
   * @func 选择门店后原生跳转到h5的数据回显
   */
  selectStoreInfoBack: () => invoke('selectStoreInfoBack', '', true),

  /**
   * @func 重新登录
   */
  toReStartLogin: (params) => invoke('toReStartLogin', params),

  /**
   * @func 上报页面统计
   */
  reportPageStatistics: (params) => invoke('reportPageStatisticsFunc', params),

  /**
   * @func 跳转到账单详情
   */
  jumpToStatisticsDetail: (params) => invoke('jumpToStatisticsDetail', params),

  /**
   * @func 自定义tab点击
   */
  customTabClick: (params) => invoke('customTabClick', params),

  /**
   * @func 判断当前桥是否可用
   */
  canIUse: (params) => invoke('canIUse', params, true),

  /**
   * @func 是否可以返回
   */
  canGoBack: () => invoke('canGoBack', '', true),

  /**
   * @func 酒店预授权打印
   */
  printCashBill: (params) => invoke('printCashBill', params),

  /**
   * @func 打开微信小程序
   */
  openMiniProgram: (params) => invoke('openMiniProgram', params),

  /**
   * @func 扫码二维码
   */
  async scanQRCode (params) {
    await invoke('scanQRCode', params)
    return register('getCodeContent')
  },

  /**
   * @func 会员模块扫码（先 register 再 call，避免回调竞态）
   * @description 国补/小票等请继续用 scanQRCode，互不影响。
   * 仅通过 getCodeContent 回传结果；勿用 callHandler 回传结算（部分壳打开相机时会先 ack，会误提前 resolve）。
   */
  scanQRCodeForMember (params) {
    const data = params == null
      ? ''
      : (typeof params === 'string' ? params : normalizeAndroidScanOptions({ ...params }))
    if (data && typeof data === 'object') {
      console.log('[bridge] scanQRCodeForMember', JSON.stringify(data))
    }
    return new Promise((resolve) => {
      let settled = false
      const finish = (res) => {
        if (settled) return
        settled = true
        const parsed = parseBridgeScanResult(res)
        console.log('[bridge] scanQRCodeForMember.result', parsed)
        resolve(parsed)
      }
      // 保持 setupMemberWebViewBridge：兼容 BridgeYes / Android 轮询（实体卡、扫会员码依赖）
      setupMemberWebViewBridge((bridge) => {
        bridge.registerHandler('getCodeContent', (res) => {
          finish(res)
        })
        bridge.callHandler('scanQRCode', data, function () {})
      })
    })
  },

  /**
   * @func 调用浏览器的返回事件，理解成就是模拟点击了物理返回键
   */
  appWebviewBack: (params) => invoke('appWebviewBack', params),

  /**
   * @func h5通知原生新的url，参数有url， type-1表示商户贷页面，2跳转到活动详情，3跳转到消息详情；fromSource: 1-商户贷，2-个人贷
   */
  originalOpenUrl: (params) => invoke('originalOpenUrl', params),
  /**
   * @function 打开原生url，参数有url,
   * @param { Object } params type: 1 默认 2 提现银行卡  3.门店管理 4.协议；isHiddenNav：是否隐藏导航栏；navTitle：导航栏标题；url: 跳转链接
   * @returns
   */
  openUrlFromH5: (params) => invoke('openUrlFromH5', params),
  /**
   * @func h5埋点通知原生，type为1，表示是活动报名，itemId: 列表id（比如活动列表id）,pagetype,1表示从活动列表，2表示从消息详情，为了配合type等于1的去情况
   */
  statisticPoint: (params) => invoke('statisticPoint', params),
  /**
   * @func 使用系统导航
   */
  useSystemNavagation: (params) => invoke('useSystemNavagation', params),
  selectBranchLocation: function (params, callBack) {
    invoke('selectBranchLocation', params, function (res) {
      console.log('********selectBranchLocation************', res)
      callBack(JSON.parse(res))
    })
    register('getBranchLocation', function (res) {
      console.log('******** getBranchLocation************', res)
      callBack(JSON.parse(res))
    })
  },
  /**
   * @func 跳转到退款页面
   */
  // toRefundPage: (params) => invoke('toRefundPage', params),
  async toRefundPage(params) {
    await invoke('toRefundPage', params)
    return register('refundCallback')
  },
  /**
   * @func 选择省市区
   */
  async selectLocation(params) {
    await invoke('selectLocation', params)
    return register('getLocation')
  },
  uploadImage: function (params, callBack) {
    // params:
    //    {previewImageUrl operateAbleFlag isTailoring}  预览模式 是否可以删除 是否可以裁剪成正方形
    //    {'', false}  选择图片模式
    invoke('uploadImage', params, function (res) {
      console.log('********uploadImage************', res)
      callBack(JSON.parse(res))
    })
    register('putUploadedImage', function (res) {
      console.log('********putUploadedImage************', res)
      callBack(JSON.parse(res))
    })
  },
  // 上传视频
  async getVideoInfo (params) {
    console.log('********getVideoInfo************', params)
    await invoke('getVideoInfo', params)
    return register('putVideoInfo')
  },
  browsePic: function (params, callBack) {
    // params: {images: [图片列表]}
    console.log('多图预览桥接调用开始', params)
    invoke('browsePic', params, function (res) {
      console.log('********browsePic************', res)
      callBack(JSON.parse(res))
    })
    register('browsePicCb', function (res) {
      console.log('********browsePicCb************', res)
      callBack(JSON.parse(res))
    })
  },
  /**
 * 日期选择
 * @param {boolean} (isShow)
 */
  async getDateSelect(params, callback) {
    const result = await invoke('getDateSelect', params, callback)
    callback(result)
  },
  /**
   * 语音转文字
   */
  async getSpeechToText (params) {
    await invoke('getSpeechToText', params)
    return register('getTextBySpeech')
  },
  /**
   * 安卓语音转文字
   */
  getSpeechToAndroidText: (params) => invoke('getSpeechToText', params, true),

  /**
   * @func 是否是AI收款单助手
   */
  showAIHelper: (params) => invoke('showAIHelper', params),
  /**
   * 原生日期选择-到秒
   * @param {boolean}
   */
  getDateSelectAll: function (params, callBack) {
    invoke('getDateSelectAll', params, function (res) {
      console.log('********getDateSelectAll************', res)
      callBack(res)
    })
    register('getDateSelectAllCb', function (res) {
      console.log('********getDateSelectAllCb************', res)
      callBack(res)
    })
  },
  /**
     * 调用Ocr相机
     */
  async uploadIdCard(params, callBack) {
    await invoke('toShowUploadIdCard', params)
    const registerData = await register('cbIdCardInfo')
    callBack(registerData)
  },
  /**
   * 调用Ocr相机
   */
  uploadLicenseInfo: function (params, callBack) {
    invoke('toShowUploadIdCard', params, function (res) {
      console.log('********toShowUploadIdCard************', res)
      callBack(JSON.parse(res))
    })
    register('cbLicenseInfo', function (res) {
      console.log('******** cbLicenseInfo************', res)
      callBack(JSON.parse(res))
    })
  },
  /**
   * @func 跳转原生页面（员工业绩流水 BillPage 等）
   * @description 原生桥名与老项目一致：H5JumpToNative（非 jumpToNative）
   * @param {Object|String} params { page: 'BillPage', param: { uid, people, source: 'bill' } }
   */
  jumpToNative: (params) => {
    const data = typeof params === 'string' ? params : JSON.stringify(params)
    return invoke('H5JumpToNative', data)
  },

  /**
   * @func 扫会员码（对齐 Angular pushInfo.memberJumpScan）
   * @description 先 register 再 call；data 传空串（与 Angular 一致；勿传 null）。
   * 若原生未注册该 handler，H5 侧会走 scanQRCode 兜底（见 scanMemberCode）。
   * @returns {Promise<any>}
   */
  memberJumpScan () {
    return new Promise((resolve, reject) => {
      console.log('[memberScan] bridge.start', {
        hasWVJB: !!window.WebViewJavascriptBridge,
        hasBridgeYes: !!window.bridgeYesApi,
        wvjbCall: !!(window.WebViewJavascriptBridge && window.WebViewJavascriptBridge.callHandler),
        yesCall: !!(window.bridgeYesApi && window.bridgeYesApi.callHandler),
        available: describeNativeBridge(getAvailableNativeBridge())
      })
      let settled = false
      let callStarted = false
      const finish = (res, from) => {
        console.log('[memberScan] bridge.finish', { from, res })
        if (settled) return
        settled = true
        if (typeof res === 'string') {
          try {
            res = JSON.parse(res)
          } catch (e) { /* keep */ }
        }
        resolve(res)
      }
      const readyTimer = setTimeout(() => {
        if (!callStarted && !settled) {
          console.error('[memberScan] bridge.timeout', {
            available: describeNativeBridge(getAvailableNativeBridge())
          })
          reject(new Error('WebView bridge timeout'))
        }
      }, 2000)
      setupMemberWebViewBridge((bridge) => {
        callStarted = true
        clearTimeout(readyTimer)
        console.log('[memberScan] bridge.ready', {
          type: describeNativeBridge(bridge),
          hasCall: typeof bridge.callHandler,
          hasRegister: typeof bridge.registerHandler
        })
        // 先挂回调，再调起（避免原生秒回丢结果）
        if (typeof bridge.registerHandler === 'function') {
          bridge.registerHandler('memberJumpScanInfo', (res) => finish(res, 'memberJumpScanInfo'))
          console.log('[memberScan] bridge.registerHandler.done')
        } else {
          console.error('[memberScan] bridge.registerHandler.missing')
        }
        // 与 Angular pushInfo 一致传空串；勿传 null（部分壳不调起相机）
        bridge.callHandler('memberJumpScan', '', function (response) {
          console.log('[memberScan] bridge.callHandler.cb', response)
          if (response != null && response !== '') finish(response, 'callHandler')
        })
        console.log('[memberScan] bridge.callHandler.invoked')
      })
    })
  },

  /**
   * @func 三方 App 授权（支付宝等）
   * @description
   * - 走 setupMemberWebViewBridge（勿用仅 WVJB 的 invoke）
   * - iOS H5：getAuthFromThirdPartApp 常空跑；禁止用 location 开 alipays（会报回跳地址不一致）
   *   空跑时改走原生 jumpToBrowser openURL
   * @param {{ authUrl: string, type: string }} params
   */
  getAuthFromThirdPartApp (params) {
    const data = params || {}
    console.log('[bridge] getAuthFromThirdPartApp', data)
    return new Promise((resolve, reject) => {
      let settled = false
      const finish = (fn, val) => {
        if (settled) return
        settled = true
        fn(val)
      }
      const readyTimer = setTimeout(() => {
        console.error('[bridge] getAuthFromThirdPartApp.timeout', {
          available: describeNativeBridge(getAvailableNativeBridge()),
          hasWVJB: !!(window.WebViewJavascriptBridge && window.WebViewJavascriptBridge.callHandler),
          hasBridgeYes: !!(window.bridgeYesApi && window.bridgeYesApi.callHandler)
        })
        if (isIOSDevice() && data.authUrl) {
          openAlipayAuthViaNative(null, data.authUrl)
          finish(resolve, null)
          return
        }
        finish(reject, new Error('WebView bridge timeout'))
      }, 2000)
      try {
        setupMemberWebViewBridge((bridge) => {
          clearTimeout(readyTimer)
          const target = pickAuthNativeBridge(bridge)
          if (!target || typeof target.callHandler !== 'function') {
            console.error('[bridge] getAuthFromThirdPartApp.unavailable')
            if (isIOSDevice() && data.authUrl) {
              openAlipayAuthViaNative(null, data.authUrl)
              finish(resolve, null)
              return
            }
            finish(reject, new Error('bridge unavailable'))
            return
          }
          console.log('[bridge] getAuthFromThirdPartApp.ready', describeNativeBridge(target))
          // 与 Angular 一致传对象；原生多数不回调 JS
          target.callHandler('getAuthFromThirdPartApp', data, function (res) {
            console.log('[bridge] getAuthFromThirdPartApp.cb', res)
            finish(resolve, res)
          })
          // iOS：专用 handler 空跑时，用原生 jumpToBrowser（勿 location 开 alipays）
          scheduleIOSAuthNativeFallback(target, data.authUrl)
          finish(resolve, null)
        })
      } catch (err) {
        clearTimeout(readyTimer)
        if (isIOSDevice() && data.authUrl) {
          openAlipayAuthViaNative(null, data.authUrl)
          finish(resolve, null)
          return
        }
        finish(reject, err)
      }
    })
  },

  /**
   * @func 注册支付宝回跳刷新回调
   * @param {Function} callback
   */
  onShowAppFromAlipay (callback) {
    setupMemberWebViewBridge((bridge) => {
      bridge.registerHandler('showAppFromAlipay', (res) => {
        if (typeof res === 'string' && /^\{.+\}$/.test(res)) {
          res = JSON.parse(res)
        }
        if (typeof callback === 'function') callback(res)
      })
    })
  }
}

window.setupWebViewJavascriptBridge = setupWebViewJavascriptBridge
window.$jfs = apiList
Vue.prototype.$jfs = apiList

export default setupWebViewJavascriptBridge
